/**
 * Edit-mode body for a comment card (#2244). Auto-growing textarea,
 * character counter, Save + Cancel, and an inline save-error line.
 * Split from `comment-card.tsx` so no file exceeds the 300-LOC limit.
 */

import {
  KeyboardEvent,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import { useTranslation } from 'react-i18next';
import { Check, X } from 'lucide-react';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';

export const COMMENT_MAX_LENGTH = 800;

export interface CommentCardEditProps {
  id: string;
  initialText: string;
  saving?: boolean;
  onSave: (nextText: string) => Promise<void>;
  onCancel: () => void;
}

export function CommentCardEdit({
  id,
  initialText,
  saving = false,
  onSave,
  onCancel
}: CommentCardEditProps) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const [text, setText] = useState<string>(initialText);
  const [saveError, setSaveError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  // Auto-focus + place caret at the end when the mode opens.
  //
  // Defer one frame so the compose textarea wins any focus-restore the
  // closing popover / dropdown trigger does on cleanup. Those cleanups
  // ran AFTER our effect in the compose flow (user clicks "Add
  // comment" → `startDraft` + `setPopoverOpen(false)` on the same
  // tick), so a synchronous `.focus()` was overwritten before the
  // caret even arrived — #2244 reopen comment "the compose textarea
  // does not get focus when it opens". rAF puts us past the current
  // commit cycle so no later restore can steal from us.
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    const raf = requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(ta.value.length, ta.value.length);
    });
    return () => cancelAnimationFrame(raf);
  }, []);

  // Auto-grow the textarea to fit its content.
  useLayoutEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${ta.scrollHeight}px`;
  }, [text]);

  const submit = async () => {
    const next = text.trim();
    if (!next) return;
    setSaveError(null);
    try {
      await onSave(next);
    } catch (err) {
      const message =
        err instanceof Error && err.message
          ? err.message
          : t('an-error-occurred');
      setSaveError(message);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onCancel();
      return;
    }
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      void submit();
    }
  };

  const remaining = COMMENT_MAX_LENGTH - text.length;
  const nearLimit = remaining <= 50;

  return (
    <div className="mt-1.5">
      <textarea
        ref={textareaRef}
        value={text}
        onChange={e => setText(e.target.value.slice(0, COMMENT_MAX_LENGTH))}
        onKeyDown={handleKeyDown}
        maxLength={COMMENT_MAX_LENGTH}
        aria-label={t('edit-comment-content')}
        aria-describedby={`${id}-counter`}
        placeholder={t('write-a-comment')}
        rows={2}
        className="w-full resize-none rounded-xl border p-2 text-xs leading-relaxed focus:outline-none"
        style={{
          borderColor: getColour('border'),
          backgroundColor: getColour('firstLayer'),
          color: getColour('normalText')
        }}
      />
      <div className="mt-1 mb-2 flex items-center justify-between gap-2">
        <div
          id={`${id}-counter`}
          className="text-[11px] tabular-nums"
          style={{
            color: nearLimit
              ? getColour('activeText')
              : getColour('inactiveText')
          }}
          aria-live="polite"
        >
          {text.length}/{COMMENT_MAX_LENGTH}
        </div>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={onCancel}
            aria-label={t('cancel')}
            title={t('cancel')}
            disabled={saving}
            className="inline-flex items-center justify-center h-7 w-7 rounded-full hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-40"
            style={{
              color: getColour('normalText'),
              backgroundColor: 'transparent'
            }}
          >
            <X size={14} aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={() => void submit()}
            aria-label={saving ? t('saving') : t('save')}
            title={saving ? t('saving') : t('save')}
            disabled={saving || text.trim().length === 0}
            className="inline-flex items-center justify-center h-7 w-7 rounded-full hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-40"
            style={{
              color: getColour('firstLayer'),
              backgroundColor: getColour('activeText')
            }}
          >
            <Check size={14} aria-hidden="true" />
          </button>
        </div>
      </div>
      {saveError && (
        <p
          role="alert"
          className="text-[11px] mt-1"
          style={{ color: 'rgb(220,38,38)' }}
        >
          {saveError}
        </p>
      )}
    </div>
  );
}
